import React, { useEffect, useMemo, useState } from "react";
import ModalComponent from "../index";
import {
	Typography,
	Box,
	useTheme,
	useMediaQuery,
	Switch,
	Divider,
	CircularProgress,
	Button,
	Alert
} from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { NotificationsCreateDto } from "../../../store/services/notifications/dto/notifications.dto";
import { useAppSelector } from "../../../store";
import { getFullState as getFullAuthState } from "../../../store/slices/auth/auth.slice";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { formatDate } from "../../../utils/formatDate";
import { getDevicePlatform } from "../../../utils/getDevicePlatform";
import FeatureGuide from "../../FeatureGuide";
import {
	useLazyGetDeviceSubscriptionQuery,
	useLazyGetNotificationsSubscriptionsQuery,
	useSubscribeToNotificationsMutation,
	useUnSubscribeToNotificationsMutation
} from "../../../store/services/notifications/notifications.service";
import { subscribeFirebase, subscribeAppleWebPush } from "../../../utils/firebase";
import { getOrCreateAnonId } from "../../../utils/getOrCreateAnonId";
import ConfirmModal from "../ConfirmModal";
import { IComment } from "../../../store/services/invitations/dto/invitations.dto";
import { trackEvent } from "../../../utils/analytics";
import { getTimeZone } from "../../../utils/getTimeZone";

const NotificationsConfig = ({ onClose, myComment }: { onClose: () => void; myComment?: IComment }) => {
	const theme = useTheme();
	const location = useLocation();
	const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));
	const params = useParams();
	const id = params.id;
	const [openIOSGuide, setOpenIOSGuide] = useState<string | null>(null);
	const [confirmUnsub, setConfirmUnsub] = useState(false);
	const { user } = useAppSelector(getFullAuthState);
	const { browser, os, device, isIOS, isIPadOS, isStandalonePWA } = getDevicePlatform();
	const [isSubscribingState, setIsSubscribingState] = useState(false);
	const [subscribe, { isLoading: isSubscribing }] = useSubscribeToNotificationsMutation();
	const [unSubscribe, { isLoading: isUnsubscribing }] = useUnSubscribeToNotificationsMutation();
	const [callGetNotificationsSubscriptions, { isLoading: isFetchingSubscriptions, data: subscriptionsList }] =
		useLazyGetNotificationsSubscriptionsQuery();
	const [callFetchSub, { data: deviceSub, isLoading: isFetchingSub }] = useLazyGetDeviceSubscriptionQuery();
	const anonymous_token = getOrCreateAnonId(id || "");
	const isGuest = location.pathname.includes("/weddingInvitation");

	useEffect(() => {
		const user_email = isGuest ? myComment?.email : user?.email;
		if (id && user_email) {
			callGetNotificationsSubscriptions({ id, email: user_email }).then(({ data, error }) => {
				if (data) {
					console.log(data);
				}
				if (error) {
					console.log("error", error);
				}
			});
			callFetchSub({ id, email: user_email, anonymous_token }).then(({ data, error }) => {
				if (data) {
					console.log(data);
				}
				if (error) {
					console.log("error", error);
				}
			});
		}
	}, [id, user?.email, myComment]);

	const Subscriptions = useMemo(() => {
		if (subscriptionsList && subscriptionsList.subscriptions.length > 0) {
			return subscriptionsList.subscriptions;
		}
		return [];
	}, [subscriptionsList]);

	const toggleEnableNotifications = async () => {
		if (deviceSub && deviceSub.subscribed) {
			return setConfirmUnsub(true);
		}
		trackEvent("notifications_subscribe", {
			scope: "notifications",
			description: "subscribe to push notifications"
		});
		const user_email = isGuest ? myComment?.email : user?.email;
		const timezone = getTimeZone();
		const body: Partial<NotificationsCreateDto> = {
			invitationId: id,
			email: user_email,
			role: isGuest ? "Guest" : "Owner",
			browser,
			os,
			device,
			anonymous_token,
			timezone
		};
		setIsSubscribingState(true);
		let token: string | PushSubscriptionJSON | null | undefined = null;
		if ((isIOS || isIPadOS) && isStandalonePWA) {
			console.log("attempt to subscribe");
			token = await subscribeAppleWebPush();
			console.log("ios token", token);
			if (!token) {
				console.log("No Apple token", token);
				setIsSubscribingState(false);
				return;
			}
		} else {
			token = await subscribeFirebase();
			console.log("firebase token", token);
			if (!token) {
				console.log("No firebase token", token);
				setIsSubscribingState(false);
				return;
			}
		}

		const _body: NotificationsCreateDto = { ...body, token } as NotificationsCreateDto;
		subscribe(_body).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setIsSubscribingState(false);
			}
			if (error) {
				setIsSubscribingState(false);
				notifyError(error as IError);
			}
		});
	};

	const openIOSGuideHandler = () => {
		trackEvent("notifications_ios_owner_guide", {
			scope: "notifications",
			description: "open ios owner notifications guide"
		});
		setOpenIOSGuide("Owner");
	};
	const openIOSGuestGuideHandler = () => {
		trackEvent("notifications_ios_guest_guide", {
			scope: "notifications",
			description: "open ios guest notifications guide"
		});
		setOpenIOSGuide("Guest");
	};
	const handleConfirmUnSub = () => {
		if (!deviceSub || !id) {
			return;
		}
		trackEvent("notifications_unsubscribe", {
			scope: "notifications",
			description: "unsubscribe from push notifications"
		});
		setIsSubscribingState(true);
		const user_email = isGuest ? myComment?.email : user?.email;
		return unSubscribe({ invitationId: id, email: user_email, anonymous_token }).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setIsSubscribingState(false);
				setConfirmUnsub(false);
			}
			if (error) {
				setIsSubscribingState(false);
				notifyError(error as IError);
				setConfirmUnsub(false);
			}
		});
	};

	return (
		<ModalComponent
			open={true}
			handleCloseModal={onClose}
			width={isSmallerScreen ? "95%" : "450px"}
			isSmallerScreen={isSmallerScreen}
		>
			<Typography className={"title"}>Notifications Setup</Typography>
			{isFetchingSubscriptions || isFetchingSub ? (
				<Box mt={6} display={"flex"} justifyContent={"center"} alignItems={"center"}>
					<CircularProgress size={30} />
				</Box>
			) : (
				<Box>
					<Box width={"100%"} mt={4}>
						<Box display={"flex"} flexDirection={"column"} gap={1}>
							{isGuest && !deviceSub && (
								<Alert severity={"info"}>
									Subscribe to receive only essential wedding reminders. No app required. You can unsubscribe anytime.
								</Alert>
							)}
							{(isIOS || isIPadOS) && !isStandalonePWA && (
								<Alert severity={"info"}>
									Notifications on iPhone work only after adding this app to your Home Screen through safari browser.
								</Alert>
							)}
						</Box>
						{(((isIOS || isIPadOS) && isStandalonePWA) || !isIOS || deviceSub?.subscribed) && (
							<Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} my={2}>
								<Box display={"flex"} gap={1}>
									<Box className={"setting_content"}>
										<Typography className={"setting_label"}>Enable Notifications</Typography>
									</Box>
								</Box>
								<Box width={"40px"} height={"40px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
									{isSubscribingState || isSubscribing ? (
										<CircularProgress size={30} />
									) : (
										<Switch checked={deviceSub?.subscribed} disabled={false} onChange={toggleEnableNotifications} />
									)}
								</Box>
							</Box>
						)}
						{(isIOS || isIPadOS) && !isStandalonePWA && !deviceSub?.subscribed && (
							<Box mt={2} width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
								<Button
									fullWidth
									variant={"contained"}
									onClick={() => (isGuest ? openIOSGuestGuideHandler() : openIOSGuideHandler())}
								>
									Guide
								</Button>
							</Box>
						)}
					</Box>
					{Subscriptions.length > 0 && (
						<Box mt={2}>
							<Divider />
							<Box my={1}>
								<Typography className={"title"}>Subscriptions</Typography>
							</Box>
							<Box mt={2}>
								{Subscriptions.map(subscription => (
									<Box
										my={1}
										display={"flex"}
										justifyContent={"space-between"}
										alignItems={"center"}
										key={subscription._id}
									>
										<Typography style={{ fontSize: isSmallerScreen ? "14px" : "16px" }}>
											{subscription.os}|{subscription.device}|{subscription.browser}
										</Typography>
										<Typography style={{ fontSize: isSmallerScreen ? "14px" : "16px" }}>
											{formatDate(subscription.createdAt, subscription.timezone)}
										</Typography>
									</Box>
								))}
							</Box>
						</Box>
					)}
				</Box>
			)}
			{openIOSGuide && <FeatureGuide title="IOS Setup" type={openIOSGuide} onClose={() => setOpenIOSGuide(null)} />}
			{confirmUnsub && (
				<ConfirmModal
					open={true}
					title={"Notifications unsubscribe"}
					message={"Are you sure you want to unsubscribe from push notifications?"}
					onConfirm={handleConfirmUnSub}
					onClose={() => setConfirmUnsub(false)}
					confirmText={"Yes, Unsubscribe"}
					cancelText={"No, Cancel"}
					loading={isUnsubscribing}
				/>
			)}
		</ModalComponent>
	);
};

export default NotificationsConfig;
